import {
	ActionKey,
	MetadataValue,
	AttributeDatatype,
	TriggerMetadataProperties,
	FactType,
	CreateWorkflowResponse,
	NumericOperator,
	TimeTriggerUnitOfTime,
	SingleGenericActionMetadata,
	ArrayType,
	UpdateFieldMetadata,
	ConsoleAction,
	WebhookAction,
	SMSAction,
	EmailAction,
	ActionType,
	SingleUpdateEntityConfiguration,
	AlertSeverity,
	DisplayFact,
	RecurringTriggerParams,
	TriggerParamsValueType,
	OptimizationAction,
	UserRole,
	ActionCoreData,
	StringOperator,
	CancelTaskAction,
	CalculateAvailabilityAction,
	ShareLocationData,
	EmailSharedLocationData,
	NarrowActionableWpTimeWindowData,
	CustomWebhookData,
	RealtimeDriverLocationWebhookData,
	EventTriggerTasksManualParams
} from '@bringg/types';
import { ConditionType } from '@bringg/react-components';

import { ConditionStore, PlaceholderCondition, ArrayConditionStore } from '../stores/internal';

export type Workflow = {
	guid?: number;
	id?: number;
} & Omit<CreateWorkflowResponse, 'id'>;

export type ChangeOptions = {
	id: TriggerParamsValueType | string;
	name: string;
	dropDownKey: string;
}[];

export enum Relative {
	before = -1,
	after = 1
}

export const triggerChangeNoValuesOptions = new Set([
	TriggerParamsValueType.Changed,
	TriggerParamsValueType.Added,
	TriggerParamsValueType.Removed
]);

export enum TriggerContainer {
	Field_Changed = 'Field_Changed',
	Date_Arrived = 'Date_Arrived',
	Event_Happened = 'Event_Happened',
	Recurring = 'Recurring',
	Manual = 'Manual'
}

export type TriggerValues = (number | string)[];
type BooleanTriggerValue = boolean;
type NumberTriggerValue = number;
type StringTriggerValue = string;

export interface StringTriggerValueOperator {
	value: string;
	operator: StringOperator;
}

export type StringTriggerValues = StringTriggerValueOperator[];

export type TriggerValue =
	| TriggerValues
	| BooleanTriggerValue
	| NumberTriggerValue
	| StringTriggerValue
	| StringTriggerValues
	| RecurringTriggerParams
	| EventTriggerTasksManualParams['value'];

export type MappedTriggerData = {
	id: string;
	family: TriggerContainer;
	displayFamily: string;
	display_path: string;
	title: string;
	items?: MappedTriggerData[];
	values?: MetadataValue[];
	properties?: TriggerMetadataProperties;
	linking_key?: string;
	datatype?: AttributeDatatype;
	factType: FactType;
	display_fact: DisplayFact;
	actions: ActionType[];
};

export type ComparableConditionValue = {
	path: string;
	fact_type: FactType;
};

export type ArrayConditionValueInnerValue = string[] | number | number[] | boolean[];

export type ArrayConditionValue = {
	value: ArrayConditionValueInnerValue;
	type: ArrayType;
};
export type ConditionValues = TriggerValue | ComparableConditionValue | ArrayConditionValue;

/**
 * Conditional gate on an incompatibility rule. The rule matches only when the
 * current trigger's `param` is set to one of `values`. Absent `when` means
 * unconditional. Mirrors the rule-service wire contract.
 */
export type IncompatibilityWhen = {
	param: string;
	values: string[];
};

/**
 * Single incompatibility rule from the metadata API. The attribute should be
 * hidden when ANY rule in the array matches the current rule context (rules
 * are OR'd). At least one of triggers/actions/attributes must be populated.
 *
 * Note: triggers and attributes are typed as `string[]` because @bringg/types
 * doesn't expose a `TriggerType` or `CodeAttributeIdentifier` enum to the FE.
 * Actions use the `ActionKey` enum since it IS exported.
 */
export type IncompatibilityRule = {
	triggers?: string[];
	actions?: ActionKey[];
	attributes?: string[];
	when?: IncompatibilityWhen;
};

export type MappedAttribute = {
	type?: ConditionType;
	title: string;
	disabled?: boolean;
	options?: { key: string | number | boolean; value: string }[];
	items?: MappedAttribute[];
	changeable?: boolean;
	path: string;
	display_path: string;
	identifier: string;
	disable_sorting?: boolean;
	factType: FactType;
	container?: string;
	incompatible_with?: IncompatibilityRule[];
};

export type MappedTrigger = {
	id: string;
	changedTo: TriggerParamsValueType;
	units: TimeTriggerUnitOfTime;
	attributeValue: TriggerValue;
	relative: Relative;
	operator: NumericOperator;
	value: TriggerValue;
};

export type AtomicCondition = ConditionStore | PlaceholderCondition | ArrayConditionStore;

export type UpdateFieldActionData = UpdateFieldMetadata & ActionDataType;

export type GenericActionData = SingleGenericActionMetadata & ActionDataType;

type ActionDataType = {
	display_path: string;
	items?: [];
	disabled?: boolean;
};

export enum AutomationsTab {
	AUTOMATION = 'automation',
	TEMPLATES = 'templates',
	SYSTEM_WORKFLOWS = 'legacy-alerts'
}

export enum ActionFamilyType {
	NOTIFY = 'alert',
	UPDATE_ENTITY = 'updateEntity',
	GENERIC_ACTION = 'genericAction',
	COSTUMER_NOTIFICATION = 'sharedLocation',
	EMAIL_COSTUMER_NOTIFICATION = 'emailSharedLocation',
	OPTIMIZATION = 'optimization',
	CANCEL_TASK = 'cancelTask',
	CALCULATE_AVAILABILITY_STATE = 'calculateAvailabilityState',
	NARROW_ACTIONABLE_WP_TIME_WINDOW = 'narrowActionableWpTimeWindow',
	CUSTOM_WEBHOOK = 'customWebhook',
	REALTIME_DRIVER_LOCATION_WEBHOOK = 'realtimeDriverLocationWebhook'
}

export type ClientAlertData = (ConsoleAction | WebhookAction | SMSAction | EmailAction) & {
	guid: number;
};

export type ClientOptimizationAction = { type: ActionFamilyType.OPTIMIZATION } & OptimizationAction;
export type ClientUpdateEntityAction = {
	type: ActionFamilyType.UPDATE_ENTITY;
	actions: SingleUpdateEntityConfiguration[];
};
export type ClientAlertAction = {
	type: ActionFamilyType.NOTIFY;
	title?: string;
	severity?: AlertSeverity;
	description_rich_text?: string;
	actions: {
		guid: number;
		action_type: ActionType;
		data: {
			role?: UserRole;
			isRoleBased?: boolean;
			userIds?: number[];
			title?: string;
		};
	}[];
};

export type ClientShareLocationAction = {
	type: ActionFamilyType.COSTUMER_NOTIFICATION;
	actionType?: ActionType;
	sharingMethod?: ShareLocationData['sharing_method'];
	translation?: ShareLocationData['sms']['translation'];
	privateMode?: ShareLocationData['private_mode'];
};

export type ClientEmailSharedLocationAction = {
	type: ActionFamilyType.EMAIL_COSTUMER_NOTIFICATION;
	actionType?: ActionType;
	template_id?: EmailSharedLocationData['template_id'];
	privateMode?: boolean;
};

// FE-only UI restriction (the rounding dropdown only offers 5/10/15) - @bringg/types@4.259.2+
// types round_to_minutes as a plain number (matching the BRNGG-28357 precedent), intentionally
// looser than this. Canonical source for this type - the store re-exports it from here (not the
// other way round) since the store already imports from this file and a reverse import would be
// circular.
export type RoundToMinutes = 5 | 10 | 15;

export type ClientNarrowActionableWpTimeWindowAction = {
	type: ActionFamilyType.NARROW_ACTIONABLE_WP_TIME_WINDOW;
	action_type?: ActionType;
	bufferInMinutes?: NarrowActionableWpTimeWindowData['buffer_in_minutes'];
	startBufferInMinutes?: number;
	endBufferInMinutes?: number;
	roundToMinutes?: RoundToMinutes;
	// True only for a real FF-on save where the server sent these fields itself; false when the
	// mapper mirrored them client-side from a legacy buffer for display. Distinguishes real
	// user-configured data from a display-only mirror on an FF-off save (BRNGG-55969 council).
	hasNewShapeFields?: boolean;
};

export type ClientGenericAction = {
	type: ActionFamilyType.GENERIC_ACTION;
	actions: ActionCoreData[];
};

export type ClientCancelTaskAction = {
	type: ActionFamilyType.CANCEL_TASK;
} & CancelTaskAction;

export type ClientCalculateAvailability = {
	type: ActionFamilyType.CALCULATE_AVAILABILITY_STATE;
	action_type: ActionType;
} & CalculateAvailabilityAction;

export type ClientCustomWebhook = {
	type: ActionFamilyType.CUSTOM_WEBHOOK;
} & { action_type: ActionType; data: CustomWebhookData };

export type ClientRealtimeDriverLocationWebhook = {
	type: ActionFamilyType.REALTIME_DRIVER_LOCATION_WEBHOOK;
	action_type: ActionType;
	data: RealtimeDriverLocationWebhookData;
};

export type ClientAction =
	| ClientOptimizationAction
	| ClientUpdateEntityAction
	| ClientAlertAction
	| ClientShareLocationAction
	| ClientGenericAction
	| ClientCancelTaskAction
	| ClientCalculateAvailability
	| ClientNarrowActionableWpTimeWindowAction
	| ClientEmailSharedLocationAction
	| ClientCustomWebhook
	| ClientRealtimeDriverLocationWebhook;
